import React, { useState } from 'react';
import {
  Typography,
  Card,
  List,
  Tag,
  Button,
  Empty,
  Space,
  Badge,
  message,
  Tabs,
  Tooltip
} from 'antd';
import {
  BellOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  ClockCircleOutlined,
  CheckOutlined,
  DeleteOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

interface Notification {
  id: string;
  type: 'health' | 'system' | 'reminder';
  title: string;
  content: string;
  time: string;
  isRead: boolean;
  date: string;
}

const Notifications: React.FC = () => {
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  // 生成模拟通知数据
  const generateNotifications = (): Notification[] => {
    return [
      {
        id: '1',
        type: 'health',
        title: '健康数据异常提醒',
        content: '居民张某的血压指标异常，收缩压达到165mmHg，建议关注并安排随访。',
        time: '2024-12-08 10:30',
        isRead: false,
        date: '2024-12-08'
      },
      {
        id: '2',
        type: 'system',
        title: '系统维护通知',
        content: '系统将于本周日凌晨2:00-4:00进行维护升级，期间可能无法正常访问。',
        time: '2024-12-07 14:00',
        isRead: false,
        date: '2024-12-07'
      },
      {
        id: '3',
        type: 'reminder',
        title: '数据上报提醒',
        content: '本月健康监测数据需在12月15日前完成上报，请及时处理。',
        time: '2024-12-06 09:00',
        isRead: false,
        date: '2024-12-06'
      },
      {
        id: '4',
        type: 'health',
        title: '视力筛查异常提醒',
        content: '学生李某视力筛查结果异常，左眼视力降至4.5，建议通知家长带孩子就医检查。',
        time: '2024-12-05 16:20',
        isRead: true,
        date: '2024-12-05'
      },
      {
        id: '5',
        type: 'system',
        title: '版本更新',
        content: '健康管理系统已更新至v1.0.0，新增了数据分析图表功能和数据导出功能。',
        time: '2024-12-04 11:00',
        isRead: true,
        date: '2024-12-04'
      },
      {
        id: '6',
        type: 'reminder',
        title: '设备校准提醒',
        content: '血压监测设备KR-001需要进行定期校准，请安排相关人员处理。',
        time: '2024-12-03 08:30',
        isRead: true,
        date: '2024-12-03'
      }
    ];
  };

  const [notifications, setNotifications] = useState<Notification[]>(generateNotifications());

  const filteredNotifications = filter === 'all'
    ? notifications
    : notifications.filter(n => !n.isRead);

  const unreadCount = notifications.filter(n => !n.isRead).length;

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'health':
        return <WarningOutlined style={{ color: '#ff4d4f' }} />;
      case 'system':
        return <InfoCircleOutlined style={{ color: '#1890ff' }} />;
      case 'reminder':
        return <ClockCircleOutlined style={{ color: '#faad14' }} />;
      default:
        return <BellOutlined />;
    }
  };

  const getNotificationTag = (type: string) => {
    switch (type) {
      case 'health':
        return <Tag color="error">健康提醒</Tag>;
      case 'system':
        return <Tag color="processing">系统通知</Tag>;
      case 'reminder':
        return <Tag color="warning">定时提醒</Tag>;
      default:
        return <Tag>通知</Tag>;
    }
  };

  const markAsRead = (id: string) => {
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, isRead: true } : n)
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev =>
      prev.map(n => ({ ...n, isRead: true }))
    );
    message.success('已全部标为已读');
  };

  const deleteNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
    message.success('通知已删除');
  };

  const tabItems = [
    {
      key: 'all',
      label: '全部',
    },
    {
      key: 'unread',
      label: (
        <Badge count={unreadCount} offset={[10, 0]}>
          未读
        </Badge>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={2}>
            <BellOutlined style={{ marginRight: 8 }} />
            通知中心
            {unreadCount > 0 && (
              <Badge count={unreadCount} style={{ marginLeft: 8 }} />
            )}
          </Title>
          {unreadCount > 0 && (
            <Button type="link" onClick={markAllAsRead}>
              <CheckOutlined /> 全部标为已读
            </Button>
          )}
        </div>
      </div>

      <div className="page-content">
        <Card>
          <Tabs
            activeKey={filter}
            onChange={(key) => setFilter(key as 'all' | 'unread')}
            items={tabItems}
          />

          {filteredNotifications.length > 0 ? (
            <List
              itemLayout="horizontal"
              dataSource={filteredNotifications}
              renderItem={(notification) => (
                <List.Item
                  style={{
                    background: notification.isRead ? 'transparent' : 'rgba(24, 144, 255, 0.05)',
                    padding: '16px',
                    borderRadius: 8,
                    marginBottom: 8
                  }}
                  actions={[
                    !notification.isRead && (
                      <Tooltip title="标为已读">
                        <Button
                          type="text"
                          size="small"
                          icon={<CheckOutlined />}
                          onClick={() => markAsRead(notification.id)}
                        />
                      </Tooltip>
                    ),
                    <Tooltip title="删除">
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => deleteNotification(notification.id)}
                      />
                    </Tooltip>
                  ].filter(Boolean)}
                >
                  <List.Item.Meta
                    avatar={
                      <div style={{
                        width: 40,
                        height: 40,
                        borderRadius: 8,
                        background: '#f5f5f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 18
                      }}>
                        {getNotificationIcon(notification.type)}
                      </div>
                    }
                    title={
                      <Space>
                        <Text strong={!notification.isRead}>
                          {notification.title}
                        </Text>
                        {getNotificationTag(notification.type)}
                        {!notification.isRead && (
                          <Badge status="processing" />
                        )}
                      </Space>
                    }
                    description={
                      <>
                        <Text
                          type={notification.isRead ? 'secondary' : undefined}
                          style={{ display: 'block', marginBottom: 4 }}
                        >
                          {notification.content}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {notification.time}
                        </Text>
                      </>
                    }
                  />
                </List.Item>
              )}
            />
          ) : (
            <Empty
              description={filter === 'all' ? '暂无通知' : '暂无未读通知'}
              style={{ padding: '60px 0' }}
            />
          )}
        </Card>

        {/* 健康小贴士 */}
        <Card title="💡 健康小贴士" style={{ marginTop: 24 }}>
          <List size="small">
            <List.Item>
              <Text>• 定期检查健康数据异常记录，及时跟进处理</Text>
            </List.Item>
            <List.Item>
              <Text>• 关注系统通知，了解最新功能更新</Text>
            </List.Item>
            <List.Item>
              <Text>• 按时完成数据上报，确保数据完整性</Text>
            </List.Item>
          </List>
        </Card>
      </div>
    </div>
  );
};

export default Notifications;
